import React, { Component, useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Translation } from 'react-i18next';
import moment from 'moment';
import { DropdownButton, MenuItem, Tooltip, OverlayTrigger, } from 'react-bootstrap';
import _ from 'underscore';
import { humanize, titleize } from 'underscore.string';
import OrderShipmentsStore from 'stores/OrderShipmentsStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import EditEquipmentModal from 'components/modules/equipment/EditEquipmentModal';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import Badge from '../../../ui/Badge';
import { createShipment } from '../CreateShipment';
import RemoveEquipmentModal from './RemoveEquipmentModal';
import { sluggify } from 'utils/localization/utils';
import { isLegManagementEnabled } from 'helpers/legs-helper';
const EquipmentDateField = ({ label, date, dataTestId }) => {
    if (!date) {
        return null;
    }
    return (React.createElement("div", { className: "text-grey-darker mb-2" },
        React.createElement("span", { className: "mr-2 font-bold" },
            label,
            ":"),
        React.createElement("span", { "data-testid": dataTestId }, moment(date).format('L'))));
};
EquipmentDateField.propTypes = {
    label: PropTypes.string,
    date: PropTypes.string,
};
const isBooking = ({ equipment, order, shipments }) => {
    if (_.findWhere(order.shipments_staging, {
        equipment_id: equipment.equipment_id,
    })) {
        return true;
    }
    for (let i = 0; i < shipments.length; i += 1) {
        const shipment = shipments[i];
        if (!shipment)
            return false;
    }
    return false;
};
const shipmentsHaveEquipment = (id, shipments) => {
    return (shipments.filter((shipment) => {
        const { stops } = shipment;
        if (shipment.canceled) {
            return false;
        }
        // aggregate all the equipment IDs for all stops
        // check if the list includes the target equipmentId
        return stops
            .reduce((shipmentEquipments, stop) => {
            const equipmentIds = (stop.equipments || []).map((e) => e._id);
            return shipmentEquipments.concat(equipmentIds);
        }, [])
            .includes(id);
    }).length > 0);
};
const shipmentExists = (props) => {
    const { equipment: { shipment }, } = props;
    const staging = props.order.shipments_staging;
    const id = props.equipment._id;
    return !!shipment || shipmentsHaveEquipment(id, staging);
};
const removeEquipmentTooltip = (React.createElement(Tooltip, { id: "remove" },
    React.createElement(Translation, null, (t, { i18n }) => React.createElement("p", null, t('EQUIPMENT_WARNING_IF_SHIPEMNT_CREATED')))));
const emptyToolTip = React.createElement(Tooltip, { id: "empty", bsClass: "invsible" });
class EquipmentItem extends Component {
    constructor(props) {
        super(props);
        this.handleOrderShipmentCreation = (type) => {
            // Can't create a shipment while a request is in progress. When it's done the type will be 'SUCCEEDED' or 'FAILED'.
            const disableCreateShipment = type === 'REQUESTED';
            this.setState({ disableCreateShipment });
        };
        this.handleRemoveEquipment = () => this.setState({ showRemoveModal: true });
        this.toggleEditing = () => this.setState({ isExpanded: !this.state.isExpanded });
        this.saveEditEquipment = (equipment) => {
            EquipmentActionCreators.updateEquipment(equipment);
        };
        this.handleManageEquipment = () => {
            this.setState({ showEditEquipmentModal: true });
        };
        this.handleCreateShipment = (eq) => {
            createShipment(this.props, {
                shipment_type: this.state.shipment_type,
                equipment: eq,
            });
            this.setState({ showConfirmModal: false });
        };
        this.state = {
            showConfirmModal: false,
            showRemoveModal: false,
            isExpanded: false,
            showEditEquipmentModal: false,
            disableCreateShipment: false,
        };
    }
    componentDidMount() {
        OrderShipmentsStore.addShipmentCreationListener(this.handleOrderShipmentCreation);
    }
    componentWillUnmount() {
        OrderShipmentsStore.removeShipmentCreationListener(this.handleOrderShipmentCreation);
    }
    hasShipments() {
        const { equipment: { shipment }, } = this.props;
        return !!shipment;
    }
    renderDates() {
        const { equipment: { earliest_return_date, last_free_date, earliest_empty_date, last_cut_date, }, t, } = this.props;
        return (React.createElement("div", { className: "flex-1 min-w-80px" },
            React.createElement(EquipmentDateField, { date: earliest_return_date, label: t('EARLIEST_RETURN_DATE'), dataTestId: "earliest-return-date" }),
            React.createElement(EquipmentDateField, { date: last_free_date, label: "Last Free Date", dataTestId: "last-free-date" }),
            React.createElement(EquipmentDateField, { date: earliest_empty_date, label: t('EARLIEST_DATE_TO_GET_EMPTY'), dataTestId: "earliest-date-to-get-empty" }),
            React.createElement(EquipmentDateField, { date: last_cut_date, label: t('LAST_CUT_DATE'), dataTestId: "last-cut-date" })));
    }
    renderStagedBadge() {
        const { equipment, order: { shipments_staging }, t, } = this.props;
        const equipmentInStaged = shipments_staging.some((s) => s.equipments && s.equipments.find((e) => e._id === equipment._id));
        if (!equipmentInStaged) {
            return null;
        }
        return (React.createElement(Badge, { type: "black", text: t('STAGED'), className: { 'mr-1': true }, showDelete: false }));
    }
    renderStatus() {
        const { equipment: { status }, t, } = this.props;
        return (React.createElement(Badge, { text: t(sluggify(status)), className: { 'mr-1': true }, showDelete: false, dataTestId: "equipment-status" }));
    }
    renderIndicator() {
        if (!this.hasShipments()) {
            return null;
        }
        const { equipment: { shipment }, } = this.props;
        return (React.createElement(Badge, { type: "green", text: shipment.shipment_id, showDelete: false }));
    }
    render() {
        const { order, equipment, shipments, t } = this.props;
        const eq = { _id: equipment._id, equipment_id: equipment.equipment_id };
        return (React.createElement("div", { className: "" },
            React.createElement("div", { className: "flex flex-row text-base hover:bg-grey-lighter p-2 border-b border-solid border-grey-lightest" },
                React.createElement("div", { className: "flex-1 min-w-100px" },
                    React.createElement("button", { onClick: () => this.handleManageEquipment() },
                        React.createElement("span", { className: "text-blue-dark", "data-testid": "equipment-id" }, equipment.equipment_id))),
                React.createElement("div", { className: "flex-1 min-w-80px", "data-testid": "equipment-type" }, humanize(equipment._type)),
                React.createElement("div", { className: "flex-1 min-w-80px", "data-testid": "equipment-size" }, equipment._equipmentSize),
                this.renderDates(),
                React.createElement("div", { className: "flex-1 flex items-center" },
                    this.renderStatus(),
                    this.renderStagedBadge(),
                    this.renderIndicator()),
                React.createElement("div", { className: "min-w-140px" },
                    React.createElement(DropdownButton, { id: "manage", title: t('MANAGE'), style: {
                            backgroundColor: 'white',
                            color: '#25AA82',
                            borderColor: '#25AA82',
                            fontSize: '.875rem',
                            fontWeight: '700',
                            borderRadius: '.25rem',
                        }, onSelect: (key) => {
                            if (key === 'remove') {
                                this.handleRemoveEquipment();
                            }
                            if (key === 'edit') {
                                this.handleManageEquipment();
                            }
                            if (key === 'book dray' || key === 'book rail') {
                                const shipment_type = key === 'book dray' ? 'drayage' : 'rail';
                                if (shipmentExists(this.props)) {
                                    this.setState({ showConfirmModal: true, shipment_type });
                                }
                                else {
                                    createShipment(this.props, {
                                        shipment_type,
                                        equipment: eq,
                                    });
                                }
                            }
                        } },
                        React.createElement(MenuItem, { eventKey: "edit", style: { fontSize: '.875rem' } }, t('EDIT_DETAILS')),
                        React.createElement(MenuItem, { id: "order-add-shipment-equipment-button", eventKey: "book dray", disabled: this.state.disableCreateShipment ||
                                isLegManagementEnabled(this.props.order), style: { fontSize: '.875rem' } }, t('CREATE_DRAY_SHIPMENT')),
                        this.props.order.mode === 'intermodal' && (React.createElement(MenuItem, { id: "order-add-shipment-equipment-button", eventKey: "book rail", disabled: this.state.disableCreateShipment ||
                                isLegManagementEnabled(this.props.order), style: { fontSize: '.875rem' } }, t('CREATE_RAIL_SHIPMENT'))),
                        isBooking(this.props) ? (React.createElement(OverlayTrigger, { placement: "top", overlay: isBooking(this.props)
                                ? removeEquipmentTooltip
                                : emptyToolTip },
                            React.createElement(MenuItem, { id: "order-remove-equipment-button", eventKey: "remove", style: { fontSize: '.875rem' }, disabled: isBooking(this.props) ||
                                    shipmentsHaveEquipment(equipment._id, shipments) }, titleize(t('REMOVE'))))) : (React.createElement(MenuItem, { eventKey: "remove", style: { fontSize: '.875rem', zIndex: 1000 }, disabled: isBooking(this.props) ||
                                shipmentsHaveEquipment(equipment._id, shipments) }, titleize(t('REMOVE')))))),
                this.state.showEditEquipmentModal && (React.createElement(EditEquipmentModal, { show: this.state.showEditEquipmentModal, onHide: () => this.setState({ showEditEquipmentModal: false }), order: order, equipment: equipment, onSave: (eq) => this.saveEditEquipment(eq) })),
                this.state.showRemoveModal && (React.createElement(RemoveEquipmentModal, { show: this.state.showRemoveModal, onClose: () => this.setState({ showRemoveModal: false }), orderId: order._id, equipmentId: equipment.equipment_id, _id: equipment._id })),
                this.state.showConfirmModal && (React.createElement(ConfirmModal, { show: this.state.showConfirmModal, title: t('SHIPMENT_CONFIRMATION'), message: t('SHIPMENT_CONTAINS_EQUIPMENT_WARNING'), onConfirm: () => this.handleCreateShipment(eq), onHide: () => this.setState({ showConfirmModal: false }) })))));
    }
}
EquipmentItem.propTypes = {
    order: PropTypes.object,
    equipment: PropTypes.object,
};
export default withTranslation()(EquipmentItem);
